/**
 * Whether the realtime socket can deliver an event right now.
 *
 * `FrappeProvider` (`main.tsx`) only creates a socket at all when `enableSocket` is true — off by
 * default against the mock, which has nothing listening on `/socket.io` (`useRealtime.ts`'s
 * header), on by default whenever a live backend is configured. `context.socket` is `undefined`
 * in the former case and a real `socket.io-client` instance — which may still be mid-handshake,
 * or drop and reconnect later — in the latter, so 'disabled' and 'disconnected' are genuinely
 * different situations worth telling apart in the UI.
 *
 * Built for `useMailExtraction.ts`: an extraction whose result depends solely on `extract.done`
 * (see that file) can never resolve on a socket that will never receive anything, and that has to
 * be detected rather than left to time out silently.
 */
import { useContext, useEffect, useState } from 'react'
import { FrappeContext } from 'frappe-react-sdk'

export type SocketStatus = 'disabled' | 'connected' | 'disconnected'

export function useSocketStatus(): SocketStatus {
  const context = useContext(FrappeContext)
  const socket = context?.socket
  const [connected, setConnected] = useState<boolean>(() => socket?.connected ?? false)

  useEffect(() => {
    if (!socket) return
    // The socket may have connected/disconnected between the initial `useState` read above and
    // this effect running — resync before subscribing.
    setConnected(socket.connected)
    const onConnect = () => setConnected(true)
    const onDisconnect = () => setConnected(false)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    return () => {
      // Handler-scoped `off` — unlike `useFrappeEventListener`'s (see `extractDoneBus.ts`'s
      // header), this removes only these two listeners, never another consumer's.
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
    }
  }, [socket])

  if (!socket) return 'disabled'
  return connected ? 'connected' : 'disconnected'
}
